// Kleinigkeiten, die mehrere Bildschirme brauchen.
import {
  EXPIRY_LABEL,
  FRESHNESS_LABEL,
  expiryLevel,
  formatNumber,
  relativeDays,
} from '@speis/shared';
import type { Freshness, ProductView } from '@speis/shared';
import { el } from '../ui/dom.js';

/** Herkunfts-Abzeichen für Einkaufslisten-Einträge. */
export const SOURCE_BADGE: Record<string, { label: string; class: string } | null> = {
  manual: null,
  siri: { label: 'Siri', class: 'badge-info' },
  auto_minstock: { label: 'Meldebestand', class: 'badge-warn' },
  auto_weekplan: { label: 'Wochenplan', class: 'badge-brand' },
  auto_replenish: { label: 'Rhythmus', class: 'badge-accent' },
  auto_expiry: { label: 'Läuft ab', class: 'badge-danger' },
  photo_diff: { label: 'Foto', class: 'badge-accent' },
  receipt: { label: 'Bon', class: 'badge' },
};

export function freshnessDot(freshness: Freshness): HTMLElement {
  return el('span', {
    class: `dot dot-${freshness}`,
    title: FRESHNESS_LABEL[freshness],
    'aria-hidden': 'true',
  });
}

/** Ablauf-Abzeichen: „läuft ab morgen", „abgelaufen" … */
export function expiryBadge(daysLeft: number | null | undefined): HTMLElement | null {
  if (daysLeft == null) return null;
  const level = expiryLevel(daysLeft);
  if (level === 'ok') return null;
  const cls = level === 'expired' ? 'badge-danger' : level === 'critical' ? 'badge-warn' : 'badge';
  const label =
    level === 'expired' ? `abgelaufen ${relativeDays(daysLeft)}` : `MHD ${relativeDays(daysLeft)}`;
  return el('span', { class: `badge ${cls}` }, label);
}

export function expiryLabel(daysLeft: number | null | undefined): string {
  if (daysLeft == null) return 'ohne Datum';
  return EXPIRY_LABEL[expiryLevel(daysLeft)];
}

/** „2× ganz + 350 ml offen" */
export function describeStock(p: ProductView): string {
  const parts = [`${p.closedCount}× ganz`];
  if (p.openRemaining != null && p.openRemaining > 0 && p.packageSizeValue) {
    parts.push(`+ ${formatNumber(p.openRemaining)} ${p.packageSizeUnit ?? ''} offen`);
  }
  return parts.join(' ');
}

/** Produktbild oder ein Platzhalter mit dem Kategorie-Symbol. */
export function productThumb(p: ProductView, categoryIcon: string): HTMLElement {
  if (p.imageUrl) {
    return el('img', {
      class: 'item-thumb',
      src: p.imageUrl,
      alt: '',
      loading: 'lazy',
      'aria-hidden': 'true',
    });
  }
  return el('span', { class: 'item-thumb thumb-fallback', 'aria-hidden': 'true' }, categoryIcon);
}

/** Ladeskelett – ersetzt „⏳"-Texte. */
export function skeletonList(rows = 4): HTMLElement {
  const wrap = el('div', { 'aria-hidden': 'true' });
  for (let i = 0; i < rows; i++) wrap.append(el('div', { class: 'skeleton skeleton-row' }));
  return wrap;
}

export function sectionTitle(title: string, action?: Node | null): HTMLElement {
  return el('div', { class: 'section-title' }, [el('span', {}, title), action ?? null]);
}

/** Datumsfeld mit sinnvoller Vorbelegung (Mindesthaltbarkeit). */
export function dateInput(
  value: string | null,
  label = 'Mindestens haltbar bis',
): {
  field: HTMLElement;
  input: HTMLInputElement;
} {
  const input = el('input', {
    class: 'input',
    type: 'date',
    value: value ?? '',
    'aria-label': label,
  });
  return {
    field: el('div', { class: 'field' }, [el('label', {}, label), input]),
    input,
  };
}
